import type { Project, ProjectFigmaFile } from '@overlayqa/shared';
import { useApiQuery } from './useApiQuery';
import { useApiMutation } from './useApiMutation';
import { useUser } from '../lib/auth';
import { projectsApi } from '../lib/api';
import { markProjectDeleted } from '../lib/recentlyDeletedProjects';
import { postExtensionProjectContext } from '../lib/extensionProjectContext';
import { log } from '@/lib/logger';

log.info('[useProjects] Module loaded');

export function useProjects(teamId?: string, options?: { includePreviews?: boolean; includeCounts?: boolean; includeArchived?: boolean; allTeams?: boolean }) {
  const { user } = useUser();
  const userId = user?.id;
  log.info('[useProjects] useProjects called', { teamId, userId });

  return useApiQuery({
    queryKey: ['projects', teamId, options?.includePreviews, options?.includeCounts, options?.includeArchived, options?.allTeams],
    queryFn: (token) =>
      projectsApi.list(token, teamId, {
        includePreviews: options?.includePreviews,
        includeCounts: options?.includeCounts,
        includeArchived: options?.includeArchived,
        userId: userId || undefined,
      }),
    // Schedule spans every accessible team; other screens wait for a team selection.
    enabled: !!teamId || options?.allTeams === true,
    placeholderPreviousData: true,
  });
}

export function useProject(id: string) {
  log.info('[useProjects] useProject called with id:', { id });
  return useApiQuery({
    queryKey: ['projects', id],
    queryFn: (token) => projectsApi.get(token, id),
    enabled: !!id,
  });
}

export function useCreateProject() {
  log.info('[useProjects] useCreateProject called');
  return useApiMutation<
    Project,
    { teamId: string; name: string; slug: string; figmaFileKey?: string; figmaNodeId?: string; primaryDomain?: string; reviewEntryUrl?: string }
  >({
    mutationFn: (token, data) => projectsApi.create(token, data),
    invalidateKeys: [['projects']],
    // Hand the new project to the extension so the sidebar's selected project
    // follows the create. Without this, workflow bulk creates keep posting to
    // the previously selected project (2026-08-02 misfile: 34 issues into the
    // wrong project ~75s after a dashboard create). Centralized here so every
    // consumer of the create mutation hands off, not just the modal.
    onSuccess: (created) => {
      postExtensionProjectContext(created, 'project_created');
    },
  });
}

export function useUpdateProject() {
  log.info('[useProjects] useUpdateProject called');
  return useApiMutation<
    unknown,
    {
      id: string;
      data: Partial<{
        name: string;
        description: string;
        figmaFileKey: string | null;
        figmaNodeId: string | null;
        icon: string | null;
        iconColor: string | null;
        primaryDomain: string | null;
        reviewEntryUrl: string | null;
        visibility: 'private' | 'team';
        clientReviewWelcomeEnabled: boolean;
        clientReviewWelcomeNote: string | null;
        clientReviewPrivateComments: boolean;
        clientReviewAllowResponsive: boolean;
      }>;
    }
  >({
    mutationFn: (token, { id, data }) => projectsApi.update(token, id, data),
    invalidateKeys: [['projects']],
    onSuccess: (_data, variables) => {
      log.info('[useProjects] useUpdateProject onSuccess for project:', { id: variables.id });
    },
  });
}

// The project's Figma FILE LIST (spec 2026-09-10). All three invalidate
// ['projects'], which covers the list queries AND ['projects', id]: the
// project row carries `figmaFiles` and the mirrored `figmaFileKey`, so a
// stale row would show the old default beside the new list.
export function useAddProjectFigmaFile() {
  return useApiMutation<
    ProjectFigmaFile[],
    { projectId: string; data: { fileKey: string; nodeId?: string | null; name?: string | null; makeDefault?: boolean } }
  >({
    mutationFn: (token, { projectId, data }) => projectsApi.figmaFiles.add(token, projectId, data),
    invalidateKeys: [['projects']],
  });
}

export function useSetDefaultProjectFigmaFile() {
  return useApiMutation<ProjectFigmaFile[], { projectId: string; fileId: string }>({
    mutationFn: (token, { projectId, fileId }) => projectsApi.figmaFiles.setDefault(token, projectId, fileId),
    invalidateKeys: [['projects']],
  });
}

export function useRemoveProjectFigmaFile() {
  return useApiMutation<ProjectFigmaFile[], { projectId: string; fileId: string }>({
    mutationFn: (token, { projectId, fileId }) => projectsApi.figmaFiles.remove(token, projectId, fileId),
    invalidateKeys: [['projects']],
  });
}

export function useRefreshProjectThumbnail() {
  return useApiMutation<{ thumbnailUrl: string }, string>({
    mutationFn: (token, id) => projectsApi.refreshThumbnail(token, id),
    // The card thumbnail comes from the list query and the modal's from the
    // single-project query; both live under ['projects'].
    invalidateKeys: [['projects']],
  });
}

export function useDeleteProject() {
  log.info('[useProjects] useDeleteProject called');
  return useApiMutation<unknown, string>({
    mutationFn: (token, id) => projectsApi.delete(token, id),
    invalidateKeys: [['projects']],
    // Mark the id so the deleted-project 404 recovery (useProjectNotFoundRedirect)
    // suppresses its toast for this intentional self-delete: the invalidate above
    // refetches the mounted useProject(id) query, which 404s. The delete handler
    // shows its own "Project deleted" success, so the recovery toast would only
    // contradict it.
    onSuccess: (_data, id) => markProjectDeleted(id),
  });
}
